import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { StatusBadge, WritingBadge } from "../components/StatusBadge";
import { fmtDate, fmtScore } from "../lib/format";

export default function AssessmentsPage() {
  const [q, setQ] = useState("");
  const [home, setHome] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });
  const { data: lookups } = useQuery({ queryKey: ["lookups"], queryFn: api.lookups });
  const { data: rows, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["assessments", q, home, status, from, to],
    queryFn: () => api.assessments.list({ q, home, status, from, to }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-liaise-navy">Assessments</h1>
          <p className="text-sm text-slate-500">
            {me?.sees_all
              ? "All assessments"
              : me?.is_manager
                ? `Assessments you created, plus ${me.manager_homes.join(", ")}`
                : "Assessments you created"}
            {me && me.pending_evaluations > 0 && (
              <>
                {" · "}
                <Link to="/evaluate" className="text-liaise-blue hover:underline">
                  {me.pending_evaluations} awaiting evaluation
                </Link>
              </>
            )}
          </p>
        </div>
        <Link to="/new" className="btn-primary">+ New assessment</Link>
      </div>

      <div className="card p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="col-span-2 md:col-span-1">
          <label className="label" htmlFor="filter-q">Search</label>
          <input id="filter-q" className="input" placeholder="Name, email or Test ID" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="filter-home">Home</label>
          <select id="filter-home" className="input" value={home} onChange={(e) => setHome(e.target.value)}>
            <option value="">All</option>
            {(me && !me.sees_all && me.is_manager
              ? (lookups?.home ?? []).filter((h) => me.manager_homes.some((m) => m.toLowerCase() === h.value.toLowerCase()))
              : (lookups?.home ?? [])
            ).map((h) => (
              <option key={h.id} value={h.value}>{h.value}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="filter-status">Status</label>
          <select id="filter-status" className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="created">Link created</option>
            <option value="in_progress">In progress</option>
            <option value="submitted">Awaiting evaluation</option>
            <option value="evaluated">Evaluated</option>
          </select>
        </div>
        <div>
          <label className="label" htmlFor="filter-from">From</label>
          <input id="filter-from" type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="filter-to">To</label>
          <input id="filter-to" type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      {error && <p className="text-liaise-red text-sm">{String(error)}</p>}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="px-4 py-3">Candidate</th>
              <th className="px-4 py-3">Test ID</th>
              <th className="px-4 py-3">Home</th>
              <th className="px-4 py-3">Position</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">English</th>
              <th className="px-4 py-3">Maths</th>
              <th className="px-4 py-3">Writing</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
            ) : error ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-liaise-red">Assessments could not be loaded. Use Refresh to try again.</td></tr>
            ) : (rows ?? []).length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No assessments found.</td></tr>
            ) : (
              rows!.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-liaise-navy-50/50">
                  <td className="px-4 py-3">
                    <Link to={`/assessment/${r.id}`} className="font-semibold text-liaise-navy hover:underline">
                      {r.first_name} {r.last_name}
                    </Link>
                    <div className="text-xs text-slate-400">{r.email ?? ""}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{r.test_id}</td>
                  <td className="px-4 py-3">{r.home ?? "-"}</td>
                  <td className="px-4 py-3">{r.position ?? "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.assessment_date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtScore(r.english_total, r.english_max)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtScore(r.maths_total, r.maths_max)}</td>
                  <td className="px-4 py-3"><WritingBadge result={r.writing_result} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="text-right">
        <button className="btn-secondary text-xs" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? "Refreshing..." : "Refresh"}
        </button>
      </div>
    </div>
  );
}
